import { VStack, Text, Flex, Box, IconButton } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Collapse } from 'react-collapse';
import { useState, useEffect } from 'react';
import debounce from 'lodash.debounce';

import { getDashboardBlocks } from 'redux/DashboardSliceV2';
import Checkbox from 'components/Checkbox';

import EditTitle from '../EditTitle';

export interface Properties {
  selectedIndicators: Array<any>;
  handleAddIndicator: (indicator: any) => void;
  handleRemoveIndicator: (indicator: any) => void;
}
const IndicatorMultiSelector = ({
  selectedIndicators,
  handleAddIndicator,
  handleRemoveIndicator,
}: Properties) => {
  const blockDetails = useSelector(getDashboardBlocks);
  const [expanded, setExpanded] = useState<string>('');
  const [searchString, setSearchString] = useState<string>('');
  const [localDetails, setLocalDetails] = useState<any[]>([]);

  useEffect(() => {
    setLocalDetails(blockDetails);
  }, [blockDetails]);

  const handleIndicator = (indicator: any) => {
    if (indicator) {
      const isSelected = selectedIndicators.some(
        (selected: any) => selected.indicator_id === indicator.indicator_id,
      );
      if (isSelected) {
        handleRemoveIndicator(indicator);
      } else {
        handleAddIndicator({
          ...indicator,
          position: selectedIndicators.length,
        });
      }
    }
  };

  const handleSearch = (event: any) => {
    if (!event) {
      setLocalDetails(blockDetails);
      return;
    }
    const detailsArray: any[] = Array.isArray(blockDetails) ? blockDetails : [];
    const objectArray = detailsArray
      .map((block: any) => {
        const blockMatches = block.block_name.toLowerCase().includes(event.toLowerCase());
        const filteredIndicators = block.indicators.filter((indicator: any) =>
          indicator.indicator_name.toLowerCase().includes(event.toLowerCase()),
        );

        if (blockMatches) {
          return { ...block };
        }
        if (filteredIndicators.length > 0) {
          return { ...block, indicators: filteredIndicators };
        }
        return null;
      })
      .filter(Boolean);
    setLocalDetails(objectArray);
    setSearchString(event);
  };

  return (
    <VStack
      align='start'
      spacing={0}
      width={'100%'}
      maxH={'500px'}
      overflowY='scroll'
      className='scroll'
    >
      <Box mb={5} w='full'>
        <EditTitle
          showLabel={false}
          title={searchString}
          onTitleChange={debounce(handleSearch, 300)}
          placeholder='Search'
        />
      </Box>

      {localDetails.map((block: any) => {
        const isIndicatorsSelected = block.indicators.some((indicator: any) =>
          selectedIndicators.some(
            (selected: any) => selected.indicator_id === indicator.indicator_id,
          ),
        );
        return (
          <VStack key={block.block_id} align='start' spacing={0}>
            <Flex
              gap={1}
              alignItems='start'
              p={1}
              width='100%'
              onClick={() => setExpanded(expanded === block.block_id ? '' : block.block_id)}
            >
              <IconButton
                aria-label={expanded !== block.block_id ? 'Collapse' : 'Expand'}
                icon={expanded !== block.block_id ? <ChevronDownIcon /> : <ChevronRightIcon />}
                size='xs'
                variant='ghost'
              />
              <Text
                variant='secondary'
                fontFamily='regular_roboto'
                fontSize='sm'
                color={'black'}
                title={block.block_name}
                fontWeight={isIndicatorsSelected ? 'bold' : 'normal'}
              >
                {block.block_name?.length > 24
                  ? `${block.block_name.slice(0, 24)}...`
                  : block.block_name}{' '}
                ({block?.indicators?.length})
              </Text>
            </Flex>
            <Collapse isOpened={expanded === block.block_id} width={'100%'}>
              <VStack align='start' spacing={2} pl={'2rem'} width={'100%'}>
                {block.indicators.map((indicator: any) => {
                  const isSelected = selectedIndicators.some(
                    (selected: any) => selected.indicator_id === indicator.indicator_id,
                  );
                  return (
                    <Checkbox
                      size='lg'
                      styles={{ paddingTop: 2 }}
                      isChecked={isSelected}
                      onChange={() => {
                        handleIndicator(
                          isSelected
                            ? indicator
                            : {
                                indicator_id: indicator.indicator_id,
                                indicator_name: indicator.indicator_name,
                                position: selectedIndicators.length,
                                block_id: block.block_id,
                                dimension_id: null,
                                dimension_name: '',
                                formatting: {},
                                aggregate_function: 'sum',
                              },
                        );
                      }}
                      label={
                        <Text
                          title={indicator.indicator_name}
                          variant='secondary'
                          fontFamily='regular_roboto'
                          fontSize='xs'
                          color={'black'}
                        >
                          {indicator.indicator_name?.length > 24
                            ? `${indicator.indicator_name.slice(0, 24)}...`
                            : indicator.indicator_name}
                        </Text>
                      }
                    />
                  );
                })}
              </VStack>
            </Collapse>
          </VStack>
        );
      })}
    </VStack>
  );
};
export default IndicatorMultiSelector;
